import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Typography,
} from '@mui/material';
import { CalendarToday, FiberNew } from '@mui/icons-material';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import { ClientExpertPostListItem } from '@/types/expert-posts';
import { getImageUrl } from '@/utils/image-url.util';

interface ExpertPostCardProps {
  post: ClientExpertPostListItem;
  onClick: () => void;
}

export const ExpertPostCard: React.FC<ExpertPostCardProps> = ({ post, onClick }) => {
  const { t } = useTranslation();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
      }}
    >
      {!post.isRead && (
        <Chip
          label={t('expertPosts.card.new')}
          color="error"
          size="small"
          icon={<FiberNew />}
          sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1, fontWeight: 'bold' }}
        />
      )}
      <CardActionArea
        onClick={onClick}
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
      >
        {post.coverImageUrl && (
          <CardMedia
            component="img"
            height="200"
            image={getImageUrl(post.coverImageUrl)}
            alt={post.title}
            sx={{ objectFit: 'cover', width: '100%' }}
          />
        )}
        <CardContent sx={{ flexGrow: 1, width: '100%' }}>
          {post.publishedAt && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {format(new Date(post.publishedAt), 'd MMMM yyyy', { locale: uk })}
              </Typography>
            </Box>
          )}
          <Typography
            gutterBottom
            variant="h6"
            component="div"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: post.coverImageUrl ? 2 : 4,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              minHeight: post.coverImageUrl ? '2.6em' : 'auto',
              fontWeight: post.isRead ? 500 : 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
            }}
          >
            {post.title}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
